import { useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

// Reports older than this are moved out of the cluster into the Atlas Online Archive
// (archive rule on Reports.timestamp). Keep in sync with the archive rule in Atlas.
export const ONLINE_ARCHIVE_DAYS = 120;

// True if `date` reaches the online archive. Uses a one-day margin (cutoff = 119 days ago):
// the backend decides with exact UTC times on dates it snaps to the end of the day, so near
// the boundary the margin guarantees the frontend asks (and sends use_archive=true) for
// everything the backend treats as archived, rather than getting a 428 refusal.
export function isInOnlineArchive(date) {
  if (!date) return false;
  const cutoff = Date.now() - (ONLINE_ARCHIVE_DAYS - 1) * 24 * 60 * 60 * 1000;
  return new Date(date).getTime() < cutoff;
}

function formatDate(date) {
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Gate for anything that would query the online archive. Call `confirmIfArchived(date, apply)`
 * instead of applying a new date directly: recent dates apply immediately, archived dates open
 * a confirmation dialog and only apply if the user chooses to continue. Render `archiveDialog`.
 */
export function useOnlineArchiveConfirm() {
  const [pending, setPending] = useState(null); // { date, apply }

  const confirmIfArchived = (date, apply) => {
    if (!isInOnlineArchive(date)) {
      apply();
      return;
    }
    setPending({ date, apply });
  };

  // For date pickers: typing a year fires onChange with partial years (0002, 0020, 0202...),
  // so ignore those until a real date is entered rather than prompting mid-typing.
  const confirmDateChange = (date, setDate) => {
    if (!date || isNaN(new Date(date).getTime())) return;
    if (new Date(date).getFullYear() < 2000) return;
    confirmIfArchived(date, () => setDate(date));
  };

  const cancel = () => setPending(null);
  const proceed = () => {
    pending.apply();
    setPending(null);
  };

  const archiveDialog = (
    <Dialog open={!!pending} onClose={cancel} maxWidth="sm" fullWidth>
      <DialogTitle>Query the online archive?</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {pending && (
            <>
              You selected <strong>{formatDate(pending.date)}</strong>, which is more than{" "}
              {ONLINE_ARCHIVE_DAYS} days ago.
            </>
          )}
        </DialogContentText>
        <DialogContentText sx={{ mb: 2 }}>
          Reports older than {ONLINE_ARCHIVE_DAYS} days have been moved to the online archive.
        </DialogContentText>
        <DialogContentText>Are you sure you want to query the online archive?</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={cancel} autoFocus>
          Cancel
        </Button>
        <Button onClick={proceed} color="warning" variant="contained">
          Query the archive
        </Button>
      </DialogActions>
    </Dialog>
  );

  return { confirmIfArchived, confirmDateChange, archiveDialog };
}

// Reminder shown while an archived date is selected (after the user has confirmed it)
function OnlineArchiveWarning({ date }) {
  if (!isInOnlineArchive(date)) return null;
  return (
    <Alert severity="warning" sx={{ my: 2 }}>
      Showing data from the online archive (more than {ONLINE_ARCHIVE_DAYS} days ago). It may take
      noticeably longer to load.
    </Alert>
  );
}

export default OnlineArchiveWarning;
